import { Box } from "@mui/material";
import { useControlledState } from "@react-stately/utils";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useFilePickerState } from "@/components/ui-v2/FilePicker/state";
import { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import { DriveProvider } from "@/features/drive/DriveContext";
import { DriveManager } from "@/features/drive/DriveManager";
import { DriveFileFragment } from "@/features/drive/queries.graphql";
import { DriveItem, DriveItemType } from "@/features/drive/types";
import { useStateBasedDriveSearchParams } from "@/features/drive/utils";
import { ContentType, NonEmptyArray } from "@/types";
import { getObjectType } from "@/utils/libraryObjectUtils";
import { getContentTypeFromAccept } from "@/utils/mimetype";

// TODO: Fix this once we refactor the DriveManager
const convertFilePickerFileToDriveFileFragment = (
  file: FilepickerFile,
): DriveFileFragment => {
  return {
    __typename: "File",
    id: file.id,
    name: file.name,
    url: file.thumbnailSrc,
    contentType: file.contentType,
    previewUrl: file.thumbnailSrc,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
    sizeBytes: 0,
    isLiked: false,
    isGenerated: false,
    isPosted: false,
    metadata: {},
    attachmentUrls: {},
    fileStatus: "PROCESSED",
    thumbnails: null,
    createdByPrincipal: null,
    sessionId: null,
    styles: [],
    inferenceParameters: {},
  };
};

const convertDriveFileFragmentToFilepickerFile = (
  file: DriveFileFragment,
): FilepickerFile => {
  return {
    id: file.id,
    name: file.name,
    src: file.url,
    type: getObjectType(file),
    thumbnailSrc: file.previewUrl ?? file.url,
    contentType: file.contentType as ContentType,
    interactedAt: new Date(),
    source: "drive",
    width: file.metadata?.width ?? undefined,
    height: file.metadata?.height ?? undefined,
    durationSeconds: file.metadata?.durationSeconds ?? undefined,
  };
};

const SelectableItemTypes: NonEmptyArray<DriveItemType> = ["File"];

interface DriveProps {
  initialDirectory?: string;
}

export const Drive = (props: DriveProps) => {
  const { initialDirectory = "/" } = props;
  const {
    selectedFiles,
    setSelectedFile,
    setSelectedFiles,
    maxFiles,
    setSelectionAsConfirmed,
    acceptedFileTypes,
  } = useFilePickerState();

  const contentType = useMemo(() => {
    return acceptedFileTypes
      ? getContentTypeFromAccept(acceptedFileTypes)
      : undefined;
  }, [acceptedFileTypes]);

  const selectedFilesDriveItems: DriveItem[] = selectedFiles.map(
    convertFilePickerFileToDriveFileFragment,
  );

  const setSelectedFilesDriveItems = useCallback(
    (files: DriveItem[]) => {
      if (files.length === 0) {
        return setSelectedFiles([]);
      }

      const acceptedFileTypesDriveItems = files.filter((file) =>
        SelectableItemTypes.includes(file.__typename),
      );

      const filepickerFiles = acceptedFileTypesDriveItems.map((file) =>
        convertDriveFileFragmentToFilepickerFile(file as DriveFileFragment),
      );
      setSelectedFiles(filepickerFiles);
    },
    [setSelectedFiles],
  );

  const [currentDirectory, setCurrentDirectory] = useState(initialDirectory);

  const [internalSelectedItems, setInternalSelectedItems] = useControlledState<
    DriveItem[]
  >(selectedFilesDriveItems, [], setSelectedFilesDriveItems);

  const [searchParams, setSearchParams] = useStateBasedDriveSearchParams();

  // Only show files in the drive
  useEffect(() => {
    setSearchParams((params) => {
      params.set("type", "file");
      return params;
    });
  }, [setSearchParams]);

  const onActivateItem = useCallback(
    (item: DriveItem) => {
      switch (item.__typename) {
        case "Directory":
          setCurrentDirectory(item.path);
          break;
        case "File":
          if (SelectableItemTypes.includes(item.__typename)) {
            const fileToSelect = convertDriveFileFragmentToFilepickerFile(item);

            if (fileToSelect) {
              const isSelected = selectedFiles.some(
                (file) => file.id === fileToSelect.id,
              );

              if (!isSelected) {
                setSelectedFile(fileToSelect);
              }

              setSelectionAsConfirmed();
            }
          }
          break;
      }
    },
    [
      setCurrentDirectory,
      setSelectedFile,
      selectedFiles,
      setSelectionAsConfirmed,
    ],
  );

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DriveProvider
        actionsDisabled={true}
        currentDirectoryPath={currentDirectory}
        setCurrentDirectoryPath={setCurrentDirectory}
        selectableItemTypes={SelectableItemTypes}
        selectableItemCount={maxFiles}
        selectedItems={internalSelectedItems}
        setSelectedItems={setInternalSelectedItems}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        onActivateItem={onActivateItem}
        contentType={contentType}
      >
        <DriveManager viewLocation="file-picker" />
      </DriveProvider>
    </Box>
  );
};
